import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { Router } from '@angular/router';

/**
 * HTTP Error Interceptor - Functional interceptor pattern (Angular 15+)
 * Automatically catches API errors and displays toast notifications
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if component wants to handle error manually
      // Components can add custom header: 'X-Skip-Error-Interceptor': 'true'
      const skipInterceptor = req.headers.get('X-Skip-Error-Interceptor');

      if (skipInterceptor === 'true') {
        return throwError(() => error);
      }

      // Handle different error scenarios
      handleHttpError(error, toastService, router);

      // Re-throw error for component-level handling if needed
      return throwError(() => error);
    })
  );
};

/**
 * Handle HTTP errors and show appropriate toast messages
 */
function handleHttpError(
  error: HttpErrorResponse,
  toastService: ToastService,
  router: Router
): void {
  // Client-side or network error
  if (error.error instanceof ErrorEvent) {
    toastService.error(
      'A network error occurred. Please check your connection.',
      'Connection Error'
    );
    return;
  }

  // Server-side error
  switch (error.status) {
    case 0:
      // Network error or CORS issue
      toastService.error(
        'Unable to connect to the server. Please try again.',
        'Connection Failed'
      );
      break;

    case 400:
      // Bad Request
      const message400 = extractErrorMessage(error) || 'Invalid request. Please check your input.';
      toastService.warning(message400, 'Validation Error');
      break;

    case 401:
      // Unauthorized - Let authInterceptor handle this
      // authInterceptor will attempt token refresh and only logout on failure
      // Do NOT show toast, clear storage, or redirect here
      return; // Skip handling - auth interceptor owns this

    case 403:
      // Forbidden
      toastService.error(
        'You do not have permission to perform this action.',
        'Access Denied'
      );
      break;

    case 404:
      // Not Found
      const message404 = extractErrorMessage(error) || 'The requested resource was not found.';
      toastService.warning(message404, 'Not Found');
      break;

    case 409:
      // Conflict
      const message409 = extractErrorMessage(error) || 'A conflict occurred. Please refresh and try again.';
      toastService.warning(message409, 'Conflict');
      break;

    case 422:
      // Unprocessable Entity (validation error)
      const message422 = extractErrorMessage(error) || 'Validation failed. Please check your input.';
      toastService.warning(message422, 'Validation Error');
      break;

    case 429:
      // Too Many Requests
      toastService.warning(
        'Too many requests. Please slow down and try again.',
        'Too Many Requests'
      );
      break;

    case 500:
      // Internal Server Error
      toastService.error(
        'An unexpected error occurred. Our team has been notified.',
        'Server Error'
      );
      break;

    case 502:
    case 503:
    case 504:
      // Bad Gateway, Service Unavailable, Gateway Timeout
      toastService.error(
        'The service is temporarily unavailable. Please try again later.',
        'Service Unavailable'
      );
      break;

    default:
      // Unknown error
      const defaultMessage = extractErrorMessage(error) || 'An unexpected error occurred.';
      toastService.error(defaultMessage, 'Error');
  }
}

/**
 * Extract error message from API response
 * Follows GenericResponseDto pattern from the codebase
 */
function extractErrorMessage(error: HttpErrorResponse): string | null {
  // Check for GenericResponseDto pattern
  if (error.error && typeof error.error === 'object') {
    // Check for common API error message fields
    const errorObj = error.error as any;

    // Try common field names
    if (errorObj.message) return errorObj.message;
    if (errorObj.error) return errorObj.error;
    if (errorObj.detail) return errorObj.detail;
    if (errorObj.result && typeof errorObj.result === 'string') return errorObj.result;

    // Handle validation errors array
    if (Array.isArray(errorObj.errors)) {
      return errorObj.errors.join(', ');
    }

    // Handle validation errors object
    if (errorObj.errors && typeof errorObj.errors === 'object') {
      const messages = Object.values(errorObj.errors).flat();
      return messages.join(', ');
    }
  }

  return null;
}
